import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  CheckCircle as CheckCircleIcon,
  HeadsetMic as HeadsetIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import SysDateRangePicker from '../../../components/SysDateRangePicker';
import UsuariosSelect from '../../../components/selects/UsuariosSelect';
import { atendimentoService, AtendimentoConexao, AtendimentoTicket } from '../../../services/atendimentoService';
import VisualizarTicketDialog from './VisualizarTicketDialog';

interface AtendimentosFechadosDialogProps {
  open: boolean;
  onClose: () => void;
  onTicketReopened?: (ticket: AtendimentoTicket) => void;
}

const CORES = ['#5c6bc0', '#26a69a', '#ef5350', '#ff7043', '#ab47bc', '#29b6f6', '#66bb6a', '#ffa726'];

const dateToYmd = (date: Date | null) => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDataAtualRange = (): { startDate: Date; endDate: Date } => {
  const hoje = new Date();
  return {
    startDate: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()),
    endDate: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()),
  };
};

const getInitials = (name: string) => name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '??';

const fmtTime = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const isToday = d.toDateString() === new Date().toDateString();
  return isToday
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const normalizeHexColor = (color?: string | null) => {
  if (!color) return '#1565c0';
  return color.startsWith('#') ? color : `#${color}`;
};

const FechadoItem: React.FC<{
  ticket: AtendimentoTicket;
  selected: boolean;
  onClick: () => void;
}> = ({ ticket, selected, onClick }) => {
  const theme = useTheme();
  const corAvatar = CORES[(ticket.contato_id || ticket.id || 0) % CORES.length];
  const corSetor = normalizeHexColor(ticket.setor?.cor);
  const nomeContato = ticket.contato?.nome || ticket.contato?.numero || 'Sem Nome';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.2,
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        borderLeft: selected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
        bgcolor: selected ? 'rgba(1,81,196,0.07)' : 'transparent',
        '&:hover': { bgcolor: selected ? 'rgba(1,81,196,0.1)' : 'action.hover' },
      }}
    >
      <Avatar
        src={atendimentoService.getAvatarUrl(ticket.contato?.foto_perfil)}
        sx={{ bgcolor: corAvatar, width: 36, height: 36, fontSize: '0.75rem', flexShrink: 0 }}
      >
        {!ticket.contato?.foto_perfil && getInitials(nomeContato)}
      </Avatar>

      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
            {ticket.contato?.cliente_id && <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32', flexShrink: 0 }} />}
            <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.82rem' }}>
              {nomeContato}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', flexShrink: 0 }}>
            {fmtTime(ticket.data_fechamento || ticket.updatedAt)}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.7rem', mt: 0.2 }}>
          {ticket.ultima_mensagem || 'Atendimento fechado'}
        </Typography>

        <Box display="flex" alignItems="center" gap={0.5} mt={0.45} flexWrap="wrap">
          {ticket.setor && (
            <Chip label={ticket.setor.nome} size="small" sx={{ height: 18, maxWidth: 120, bgcolor: corSetor, color: '#fff', fontSize: '0.65rem', fontWeight: 600 }} />
          )}
          {ticket.conexao && (
            <Chip
              icon={<WhatsAppIcon sx={{ fontSize: '0.78rem !important' }} />}
              label={ticket.conexao.nome}
              size="small"
              sx={{ height: 18, maxWidth: 130, bgcolor: theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.16)' : 'rgba(37,211,102,0.12)', color: theme.palette.mode === 'dark' ? '#8ee6ab' : '#128c4a', fontSize: '0.65rem', fontWeight: 600 }}
            />
          )}
          {ticket.usuario && (
            <Typography variant="caption" color="primary.main" fontWeight={700} noWrap sx={{ fontSize: '0.65rem' }}>
              <HeadsetIcon sx={{ fontSize: 13, verticalAlign: 'text-bottom', mr: 0.25 }} />
              {ticket.usuario.nome}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const AtendimentosFechadosDialog: React.FC<AtendimentosFechadosDialogProps> = ({
  open,
  onClose,
  onTicketReopened,
}) => {
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<AtendimentoTicket[]>([]);
  const [conexoes, setConexoes] = useState<AtendimentoConexao[]>([]);
  const [conexaoId, setConexaoId] = useState<number | null>(null);
  const [usuarioId, setUsuarioId] = useState<number | null>(null);
  const [criacao, setCriacao] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null });
  const [fechamento, setFechamento] = useState<{ startDate: Date | null; endDate: Date | null }>(() => {
    const { startDate, endDate } = getDataAtualRange();
    return { startDate, endDate };
  });
  const [ticketVisualizacao, setTicketVisualizacao] = useState<AtendimentoTicket | null>(null);

  useEffect(() => {
    if (!open) return;

    let ativo = true;
    atendimentoService.getConexoes()
      .then((data) => {
        if (ativo) setConexoes(data);
      })
      .catch((err) => {
        console.error('Erro ao carregar conexoes para filtro', err);
        if (ativo) setConexoes([]);
      });

    return () => {
      ativo = false;
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    handleLimpar();
  }, [open]);

  const handleLimpar = () => {
    setConexaoId(null);
    setUsuarioId(null);
    setCriacao({ startDate: null, endDate: null });
    setFechamento(getDataAtualRange());
    setTickets([]);
    setTicketVisualizacao(null);
  };

  const handleBuscar = async () => {
    try {
      setLoading(true);
      const data = await atendimentoService.getTickets({
        status: 'fechado',
        conexao_id: conexaoId,
        usuario_id: usuarioId,
        data_criacao_inicio: dateToYmd(criacao.startDate),
        data_criacao_fim: dateToYmd(criacao.endDate),
        data_fechamento_inicio: dateToYmd(fechamento.startDate),
        data_fechamento_fim: dateToYmd(fechamento.endDate),
      });
      setTickets(data);
    } catch (err) {
      console.error('Erro ao buscar atendimentos fechados', err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      }}
    >
      <DialogTitle sx={{ py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ArchiveIcon fontSize="small" />
        Atendimentos fechados
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        <Grid container spacing={1} alignItems="flex-start" sx={{ mb: 1.5, pt: 0.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Conexao</InputLabel>
              <Select
                value={conexaoId ?? ''}
                label="Conexao"
                onChange={(e) => setConexaoId(e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {conexoes.map((conexao) => (
                  <MenuItem key={conexao.id} value={conexao.id}>
                    {conexao.nome}{conexao.numero ? ` - ${conexao.numero}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <UsuariosSelect
              value={usuarioId}
              onChange={setUsuarioId}
              label="Usuario"
              placeholder="Todos"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <SysDateRangePicker label="Data criacao" value={criacao} onChange={setCriacao} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <SysDateRangePicker label="Data fechamento" value={fechamento} onChange={setFechamento} />
          </Grid>

          <Grid item xs={6} sm={3} md={6}>
            <Button fullWidth size="small" onClick={handleLimpar} disabled={loading} sx={{ minHeight: 40, px: 1 }}>
              Limpar
            </Button>
          </Grid>
          <Grid item xs={6} sm={3} md={6}>
            <Button fullWidth size="small" variant="contained" onClick={handleBuscar} disabled={loading} sx={{ minHeight: 40, px: 1 }}>
              Buscar
            </Button>
          </Grid>
        </Grid>

        <Divider />

        <Box sx={{ minHeight: 220, maxHeight: '45vh', overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : tickets.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <WhatsAppIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Use os filtros e clique em Buscar
              </Typography>
            </Box>
          ) : (
            tickets.map((ticket, index) => (
              <React.Fragment key={ticket.id}>
                {index > 0 && <Divider sx={{ mx: 1.5 }} />}
                <FechadoItem
                  ticket={ticket}
                  selected={ticketVisualizacao?.id === ticket.id}
                  onClick={() => setTicketVisualizacao(ticket)}
                />
              </React.Fragment>
            ))
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>

      <VisualizarTicketDialog
        open={Boolean(ticketVisualizacao)}
        ticket={ticketVisualizacao}
        onClose={() => setTicketVisualizacao(null)}
        onTicketReopened={(ticket) => {
          setTickets((prev) => prev.filter((item) => item.id !== ticket.id));
          setTicketVisualizacao(null);
          onTicketReopened?.(ticket);
        }}
      />
    </Dialog>
  );
};

export default AtendimentosFechadosDialog;
